/**
 * The lookup-table editor's entries, as plain data (no store, no React), so
 * the parsing and checks are tested on their own.
 *
 * The API (`data-mapper-lookup-tables`) stores a source value as text and a
 * target value through its own encoding: "true"/"false" come back as
 * booleans and JSON objects as objects, everything else as the text sent. The
 * grid edits both as text and sends the target as typed.
 */

/** The API's cap on one table's entries (data-mapper-lookup-tables.schema.ts LOOKUP_ENTRIES_MAX). */
export const LOOKUP_ENTRIES_MAX = 50_000;

/** Rows the grid shows per page (a table may hold 50,000). */
export const LOOKUP_ROWS_PER_PAGE = 50;

/** One row of the grid. `key` is the grid's own, never sent. */
export interface LookupEntryRow {
  key: string;
  source: string;
  target: string;
  /**
   * The stored target was null (no value), shown as an empty box. Kept so an
   * untouched row saves null again rather than "" (Codex r1); editing the
   * target clears it.
   */
  targetIsNull?: boolean;
}

let rowSeq = 0;
export const newRowKey = (): string => `lk-${++rowSeq}`;

/** A stored target value as the grid shows it. */
export const targetText = (value: unknown): string => {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

/** The grid rows for a table's stored entries. */
export const rowsFromEntries = (entries: ReadonlyArray<{ sourceValue: unknown; targetValue: unknown }>): LookupEntryRow[] =>
  entries.map((e) => ({
    key: newRowKey(),
    source: String(e.sourceValue ?? ""),
    target: targetText(e.targetValue),
    ...(e.targetValue == null ? { targetIsNull: true } : {}),
  }));

export interface PastedEntries {
  rows: Array<{ source: string; target: string }>;
  /** Lines with only one column: there is no target value to take. */
  skipped: number;
}

/**
 * Two columns pasted from a spreadsheet (tab-separated, as Excel and Sheets
 * copy them) or typed as `source,target`: source value, then target value.
 * A third column and beyond is ignored; blank lines are dropped; a line with
 * one column is skipped and counted. Values are trimmed.
 */
export const parsePastedEntries = (text: string): PastedEntries => {
  const rows: PastedEntries["rows"] = [];
  let skipped = 0;
  for (const line of text.split(/\r?\n/)) {
    if (!line.trim()) continue;
    const sep = line.includes("\t") ? "\t" : line.includes(",") ? "," : null;
    if (!sep) {
      skipped++;
      continue;
    }
    const at = line.indexOf(sep);
    const source = line.slice(0, at).trim();
    const rest = line.slice(at + 1);
    // Tab-separated: the second column only. Comma: everything after the first comma.
    const target = (sep === "\t" ? rest.split("\t")[0] : rest).trim();
    rows.push({ source, target });
  }
  return { rows, skipped };
};

/** A row the person left entirely empty: not an entry, and not sent. */
const isBlank = (r: LookupEntryRow) => !r.source.trim() && !r.target.trim();

/** Why the entries cannot be saved as they are, or null. */
export const entriesProblem = (rows: readonly LookupEntryRow[]): string | null => {
  const filled = rows.filter((r) => !isBlank(r));
  if (filled.length > LOOKUP_ENTRIES_MAX) {
    return `A lookup table holds at most ${LOOKUP_ENTRIES_MAX.toLocaleString()} entries; this one has ${filled.length.toLocaleString()}.`;
  }
  const noSource = filled.filter((r) => !r.source.trim()).length;
  if (noSource > 0) {
    return `${noSource} row${noSource === 1 ? " has" : "s have"} a target value but no source value. Fill in the source or remove ${noSource === 1 ? "it" : "them"}.`;
  }
  return null;
};

/** How many rows repeat a source value an earlier row already has (a lookup uses the last one). */
export const duplicateSourceCount = (rows: readonly LookupEntryRow[]): number => {
  const seen = new Set<string>();
  let dupes = 0;
  for (const r of rows) {
    if (isBlank(r)) continue;
    if (seen.has(r.source)) dupes++;
    else seen.add(r.source);
  }
  return dupes;
};

/**
 * A table's entry count: its loaded entries when the cache holds them (a `get`
 * or a save answered them, so they are the newest), else the count `find` gave.
 */
export const entryCountOf = (table: { entryCount?: number; entries?: unknown[] }): number | null =>
  Array.isArray(table.entries) ? table.entries.length : typeof table.entryCount === "number" ? table.entryCount : null;

/** What `create` / `patch` send as `entries`: every non-empty row, in grid order. */
export const toEntriesPayload = (
  rows: readonly LookupEntryRow[]
): Array<{ sourceValue: string; targetValue: string | null }> =>
  rows
    .filter((r) => !isBlank(r))
    .map((r) => ({ sourceValue: r.source, targetValue: r.targetIsNull && r.target === "" ? null : r.target }));
